import { closeLangSwitcher } from "./lang-switcher";
import { closeMobileNav } from "./mobile-nav";
import { setLogoExpanded } from "./ma-logo-scroll";

function getContactPanel() {
  return document.querySelector(".js-header-contact");
}

function getSiteHeader() {
  return document.querySelector(".js-header");
}

function getContactToggles() {
  return document.querySelectorAll(".js-contact-toggle");
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

let closeTransitionHandler = null;

function clearCloseTransition(panel) {
  if (closeTransitionHandler && panel) {
    panel.removeEventListener("transitionend", closeTransitionHandler);
  }
  closeTransitionHandler = null;
}

function setContactOpen(open) {
  const panel = getContactPanel();
  const header = getSiteHeader();
  const toggles = getContactToggles();

  if (!panel || toggles.length === 0) return;

  clearCloseTransition(panel);

  if (open) {
    panel.removeAttribute("hidden");
    panel.removeAttribute("inert");
    panel.setAttribute("aria-hidden", "false");

    // Wait until [hidden] display:none is cleared before starting the transition
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.add("is-open");
      });
    });
  } else {
    panel.classList.remove("is-open");
    panel.setAttribute("inert", "");
    panel.setAttribute("aria-hidden", "true");

    const finishClose = () => {
      clearCloseTransition(panel);
      panel.setAttribute("hidden", "");
    };

    if (prefersReducedMotion()) {
      finishClose();
    } else {
      closeTransitionHandler = (event) => {
        if (event.target !== panel) return;
        if (event.propertyName !== "height") return;
        finishClose();
      };
      panel.addEventListener("transitionend", closeTransitionHandler);
    }
  }

  // Overlay/home styles key off this; solid headers ignore it in CSS.
  header?.classList.toggle("is-contact-open", open);

  // Reveal full resting logo while Contact is open; resume scroll collapse on close.
  setLogoExpanded(open);

  toggles.forEach((toggle) => {
    const openLabel = toggle.dataset.labelOpen || "Contact";
    const closeLabel = toggle.dataset.labelClose || "Close";
    const label = open ? closeLabel : openLabel;

    toggle.textContent = label;
    toggle.setAttribute("aria-label", label);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.classList.toggle("is-active", open);
  });
}

export function closeHeaderContact() {
  if (isHeaderContactOpen()) {
    setContactOpen(false);
  }
}

export function isHeaderContactOpen() {
  const panel = getContactPanel();
  return Boolean(panel && panel.classList.contains("is-open"));
}

export function initHeaderContact() {
  if (window.headerContactInitialized) {
    return;
  }

  window.headerContactInitialized = true;

  document.addEventListener("click", (event) => {
    const closeButton = event.target.closest(".js-header-contact-close");
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      closeHeaderContact();
      return;
    }

    // Close when following an in-page / same-window link (e.g. Jobs → about#jobs).
    // External social/address links use target=_blank and keep the panel open.
    const contactNavLink = event.target.closest(".c-header-contact a[href]");
    if (
      contactNavLink &&
      isHeaderContactOpen() &&
      contactNavLink.getAttribute("target") !== "_blank"
    ) {
      closeHeaderContact();
    }

    const toggle = event.target.closest(".js-contact-toggle");
    const panel = getContactPanel();

    if (toggle) {
      event.preventDefault();
      event.stopPropagation();

      const willOpen = !isHeaderContactOpen();

      if (willOpen) {
        closeLangSwitcher();
        closeMobileNav();
      }

      setContactOpen(willOpen);
      return;
    }

    if (
      panel &&
      isHeaderContactOpen() &&
      !event.target.closest(".c-site-header")
    ) {
      setContactOpen(false);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeHeaderContact();
    }
  });
}
